import type { UserSummary } from '@cult/schemas';
import { Select, Space } from 'antd';
import { UserAvatar } from '../UserAvatar/UserAvatar';

// Keeps an option (e.g. a locked/selected user not in the current search page) selectable so its
// label renders even when it's missing from the fetched results.
export type UserSearchOption = { value: string; displayName: string; avatarUrl: string | null };

type Props = {
  value?: string;
  onChange?: (value: string | undefined) => void;
  users: UserSummary[];
  loading: boolean;
  canLoadMore: boolean;
  onSearch: (term: string) => void;
  onLoadMore: () => void;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  extraOption?: UserSearchOption | null;
  style?: React.CSSProperties;
};

// ~9.5 option rows (32px each): pickers fetch 10 per page, and the half-visible last row both hints at
// more and keeps the popup scrollable, so infinite scroll can still fire for the next page.
const LIST_HEIGHT = 304;

// Renders a teammate option as an avatar next to their name (inline cluster → Space).
const userLabel = (displayName: string, avatarUrl: string | null): React.ReactNode => (
  <Space size="small">
    <UserAvatar avatarUrl={avatarUrl} size="small" />
    {displayName}
  </Space>
);

// Presentation half of every teammate picker: the searchable, infinite-scrolling Select. Callers
// own the query (admin user list vs member directory) and feed it pages through `users`.
export const UserSearchSelect = ({
  value,
  onChange,
  users,
  loading,
  canLoadMore,
  onSearch,
  onLoadMore,
  placeholder,
  disabled,
  allowClear,
  extraOption,
  style,
}: Props) => {
  // Fetch the next page once the dropdown is scrolled near the bottom (infinite scroll).
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, offsetHeight, scrollHeight } = e.currentTarget;
    if (scrollTop + offsetHeight >= scrollHeight - 32 && canLoadMore) onLoadMore();
  };

  const baseOptions = users.map((u) => ({ value: u.id, label: userLabel(u.displayName, u.avatarUrl) }));
  const options =
    extraOption && !baseOptions.some((o) => o.value === extraOption.value)
      ? [{ value: extraOption.value, label: userLabel(extraOption.displayName, extraOption.avatarUrl) }, ...baseOptions]
      : baseOptions;

  return (
    <Select
      showSearch
      value={value || undefined}
      onChange={(v) => onChange?.(v ?? undefined)}
      disabled={disabled}
      allowClear={allowClear}
      placeholder={placeholder}
      filterOption={false}
      onSearch={onSearch}
      onPopupScroll={handleScroll}
      listHeight={LIST_HEIGHT}
      notFoundContent={loading ? 'Searching…' : 'No teammates found'}
      loading={loading}
      options={options}
      style={style}
    />
  );
};
